/* eslint-disable react/no-danger -- Event description HTML is allow-list sanitized by the API before serialization. */
import * as React from 'react'
import { XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { EventRecord } from '@/pages/EventsPage'

export function EventDetailActions({
  event,
  publishing,
  unpublishing,
  cancelling,
  onCheckIn,
  onPublish,
  onUnpublish,
  onCancel,
}: {
  event: EventRecord
  publishing: boolean
  unpublishing: boolean
  cancelling: boolean
  onCheckIn: () => void
  onPublish: () => void
  onUnpublish: () => void
  onCancel: () => void
}) {
  if (event.status === 'cancelled') return null
  if (event.owner && typeof event.owner !== 'string' && event.owner.connection) return null

  return (
    <>
      {event.status === 'published' && (
        <Button type="button" onClick={onCheckIn}>
          Scan
        </Button>
      )}
      {event.status === 'draft' ? (
        <Button type="button" onClick={onPublish} disabled={publishing}>
          {publishing ? 'Publishing…' : 'Publish'}
        </Button>
      ) : (
        <>
          <Button type="button" variant="outline" onClick={onUnpublish} disabled={unpublishing}>
            {unpublishing ? 'Returning…' : 'Return to draft'}
          </Button>
          <CancelEventControl pending={cancelling} onCancel={onCancel} />
        </>
      )}
    </>
  )
}

function CancelEventControl({ pending, onCancel }: { pending: boolean; onCancel: () => void }) {
  const [open, setOpen] = React.useState(false)
  return (
    <>
      <Button type="button" variant="quietDestructive" onClick={() => setOpen(true)}>
        <XCircle className="mr-1.5 h-4 w-4" aria-hidden="true" />
        Cancel event
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel this Event?</DialogTitle>
            <DialogDescription>
              Sales stop permanently. The public page and Order Links stay available as Cancelled,
              and every Order buyer receives a cancellation notice. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Keep event
            </Button>
            <Button type="button" variant="destructive" disabled={pending} onClick={onCancel}>
              {pending ? 'Cancelling…' : 'Cancel event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function CancelledEventDetails({ event }: { event: EventRecord }) {
  return (
    <>
      <div className="rounded-md border border-border bg-muted/60 px-4 py-3 text-body-sm text-muted-foreground">
        This Event is cancelled. Its page and {event.order_count ?? 0} Orders remain as history.
      </div>
      <ReadOnlySection title="Details">
        <ReadOnlyRow label="Name" value={event.name} />
        <ReadOnlyRow label="Date & time" value={`${event.schedule} ${event.time_zone_label}`} />
        <ReadOnlyRow label="Location" value={event.location ?? '—'} />
        <ReadOnlyDescription value={event.description} />
      </ReadOnlySection>
      <ReadOnlySection title="Tickets">
        {event.ticket_types.map((ticket) => (
          <ReadOnlyRow key={ticket.id} label={ticket.name} value={ticket.price} />
        ))}
      </ReadOnlySection>
      <ReadOnlySection title="Registration">
        <ReadOnlyRow label="Sales" value="Closed permanently" />
        <ReadOnlyRow
          label="Day-before reminder"
          value={event.day_before_reminder_enabled ? 'Was enabled · now stopped' : 'Disabled'}
        />
      </ReadOnlySection>
    </>
  )
}

function ReadOnlySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-caption font-medium text-muted-foreground">{title}</h2>
      <div className="divide-y divide-border rounded-md border border-border bg-card">
        {children}
      </div>
    </section>
  )
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-2 px-4 py-3 sm:grid-cols-[148px_minmax(0,1fr)]">
      <p className="text-caption text-muted-foreground">{label}</p>
      <p className="text-body-sm text-foreground">{value}</p>
    </div>
  )
}

function ReadOnlyDescription({ value }: { value: string | null }) {
  return (
    <div className="grid gap-2 px-4 py-3 sm:grid-cols-[148px_minmax(0,1fr)]">
      <p className="text-caption text-muted-foreground">Description</p>
      {value ? (
        <div
          className="text-body-sm text-foreground [&_a]:text-primary [&_li]:ml-5 [&_li]:list-disc"
          dangerouslySetInnerHTML={{ __html: value }}
        />
      ) : (
        <p className="text-body-sm text-foreground">—</p>
      )}
    </div>
  )
}
